import type {CommentHeaderProps} from '@github-ui/commenting/CommentHeader'
import type {CommitComment} from '../../types/commit-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {encodePart} from '@github-ui/paths'
import {useCSRFToken} from '@github-ui/use-csrf-token'
import {CommentActions, type CommentData} from '@github-ui/commenting/CommentActions'
import type {Commit} from '../../types/shared'
import {ActivityHeader} from '@github-ui/commenting/ActivityHeader'

export function ExistingCommitComments({
  comments,
  commit,
  setComments,
}: {
  comments: CommitComment[]
  commit: Commit
  setComments: (comments: CommitComment[]) => void
}) {
  const deleteComment = (comment: CommitComment) => {
    setComments(comments.filter(c => c.id !== comment.id))
  }

  return (
    <div className="d-flex flex-column py-3 gap-3">
      {comments.map(comment => {
        return (
          <ExistingCommitComment key={comment.id} comment={comment} commit={commit} deleteComment={deleteComment} />
        )
      })}
    </div>
  )
}

function ExistingCommitComment({
  comment,
  commit,
  deleteComment,
}: {
  comment: CommitComment
  commit: Commit
  deleteComment: (comment: CommitComment) => void
}) {
  const repo = useCurrentRepository()

  const deleteUrl = `/${encodePart(repo.ownerLogin)}/${encodePart(repo.name)}/commit_comment/${comment.id}`
  const deleteToken = useCSRFToken(deleteUrl, 'delete')

  const onDelete = async () => {
    if (!confirm('Are you sure you want to delete this?')) {
      return
    }

    const formData = new FormData()

    formData.append('_method', 'delete')
    // eslint-disable-next-line github/authenticity-token
    formData.append('authenticity_token', deleteToken!)
    formData.append('input[id]', comment.id)
    const resonse = await verifiedFetch(deleteUrl, {
      method: 'POST',
      body: formData,
    })

    if (resonse.ok) {
      deleteComment(comment)
    } else {
      // TODO - handle error
    }
  }

  const commentSubjectAuthorLogin = commit.authors.find(author => author.login === comment.author.login)?.login

  return (
    <div className="border rounded-2">
      <CommitCommentHeader
        comment={
          {
            id: comment.id,
            body: comment.body,
            createdAt: comment.createdAt,
            url: comment.url,
            authorAssociation: comment.authorAssociation?.toUpperCase(),
            author: {
              id: comment.author.id,
              login: comment.author.login,
            },
            isHidden: comment.isHidden,
            viewerCanMinimize: comment.viewerCanMinimize,
            // These might not be correct
            viewerCanSeeMinimizeButton: comment.viewerCanMinimize,
            viewerCanSeeUnminimizeButton: comment.viewerCanMinimize,
            minimizedReason: comment.minimizedReason,
            viewerCanDelete: comment.viewerCanDelete,
            viewerCanUpdate: comment.viewerCanUpdate,
            viewerCanReport: comment.viewerCanReport,
            viewerCanReportToMaintainer: comment.viewerCanReportToMaintainer,
            viewerCanBlockFromOrg: comment.viewerCanBlockFromOrg,
            viewerCanUnblockFromOrg: comment.viewerCanUnblockFromOrg,
            reference: {
              number: 0,
            },
            repository: {
              id: repo.id.toString(),
              isPrivate: repo.private,
              name: repo.name,
              owner: {
                id: repo.ownerLogin,
                login: repo.ownerLogin,
                url: '',
              },
            },
          } as CommentData
        }
        commentAuthorLogin={comment.author.login}
        commentSubjectAuthorLogin={commentSubjectAuthorLogin}
        avatarUrl={comment.author.avatarUrl}
        isMinimized={false}
        editComment={() => {}}
        onReplySelect={() => {}}
        onMinimize={() => {}}
        navigate={() => {}}
        hideComment={() => {}}
        unhideComment={() => {}}
        deleteComment={() => {
          onDelete()
        }}
      />
      <div className="d-flex flex-column m-3 gap-3" style={{gap: '12px'}}>
        <div className="markdown-body" data-turbolinks="false">
          <MarkdownViewer
            disabled={false}
            verifiedHTML={comment.htmlBody}
            markdownValue={comment.body}
            onChange={() => {}}
            onLinkClick={() => {}}
            teamHovercardsEnabled={true}
          />
        </div>
      </div>
    </div>
  )
}

function CommitCommentHeader({hideActions, ...props}: CommentHeaderProps) {
  return (
    <ActivityHeader
      {...props}
      actions={hideActions ? undefined : <CommentActions {...props} editHistoryComponent={null} />}
    />
  )
}

try{ ExistingCommitComments.displayName ||= 'ExistingCommitComments' } catch {}
try{ ExistingCommitComment.displayName ||= 'ExistingCommitComment' } catch {}
try{ CommitCommentHeader.displayName ||= 'CommitCommentHeader' } catch {}